/**
 * UI Configuration based on user's mode (emotional approach) and expertise (knowledge level).
 * 
 * Mode axis: performance | progress | gentle (how Nuri talks to you)
 * Expertise axis: pro | intermediate | beginner (how much Nuri explains)
 * 
 * These are INDEPENDENT — you can be a Pro in Gentle Mode or a Beginner in Performance Mode.
 */

export function getUIConfig(mode = 'progress', expertise = 'intermediate') {
  const isPro = expertise === 'pro';
  const isBeginner = expertise === 'beginner';
  const isIntermediate = expertise === 'intermediate';

  return {
    // Dashboard density
    dashboardDensity: isPro ? 'compact' : isBeginner ? 'simplified' : 'standard',

    // What to show
    showEducationalCards: isBeginner,
    showTooltips: !isPro, // intermediate sees them a few times, beginner always
    showHelperLinks: isBeginner, // "What is protein?" links
    showAdvancedMetrics: isPro, // net calories, macro %, weekly averages
    showJustOneThing: isBeginner, // simplified single-action card
    showGuidedWalkthroughs: isBeginner,

    // Macro display style
    macroDisplayStyle: isPro ? 'numbers' : isBeginner ? 'bars' : 'rings',
    // 'numbers': compact data table
    // 'bars': progress bars (visual, simple)
    // 'rings': ring charts (moderate detail)

    // Meal result disclosure
    mealResultDisclosure: isPro ? 'full' : isBeginner ? 'progressive' : 'standard',
    // 'full': all nutrition data shown immediately
    // 'progressive': summary first, expand for details
    // 'standard': moderate detail shown

    // Coach verbosity
    coachVerbosity: isPro ? 'concise' : isBeginner ? 'detailed' : 'moderate',
    // 'concise': "You're at 65% protein target"
    // 'moderate': "You're at 65% protein — aim for lean sources at dinner to close the gap."
    // 'detailed': "Protein helps your muscles recover and keeps you feeling full. You've had 98g out of 150g..."

    // Card subtitle visibility (for beginners)
    showCardSubtitles: isBeginner || isIntermediate,

    // Educational tooltip dismiss threshold (intermediate: hide after 3 views)
    tooltipDismissAfter: isIntermediate ? 3 : isBeginner ? null : 0,

    // Mode context (passthrough for components that need both)
    mode,
    expertise,
  };
}

/**
 * Get a short coaching explanation based on expertise level.
 * Used by AI Coach to calibrate response verbosity.
 */
export function getCoachExpertisePrompt(expertise = 'intermediate') {
  switch (expertise) {
    case 'pro':
      return 'User is an experienced nutrition tracker. Be concise and data-focused. Skip explanations of basic concepts. Use shorthand like "65% protein target" instead of explaining what protein does.';
    case 'beginner':
      return 'User is new to nutrition tracking. Explain concepts clearly. For example, instead of "65% protein target", say "Protein helps your muscles recover and keeps you feeling full. You\'ve had 98g out of your 150g target today — about 65%." Always suggest specific foods and portions.';
    default:
      return 'User has moderate nutrition knowledge. Explain briefly when helpful. For example, "You\'re at 65% protein — aim for lean sources like chicken or fish at dinner to close the gap."';
  }
}
